import { ReactNode, ElementType, forwardRef, ComponentPropsWithoutRef } from "react";

export type Props<T extends ElementType = "div"> = {
  /** Element type to render */
  as?: T;
  /** Content to render */
  children: ReactNode;
  /** Additional CSS classes for outer container */
  className?: string;
  /** Additional CSS classes for inner container */
  innerClassName?: string;
  /** ID attribute */
  id?: string;
  /** Test ID for testing */
  "data-testid"?: string;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "children" | "className" | "id">;

/**
 * Base polymorphic content frame component with no styling.
 * Provides a two-layer structure (outer/inner) for layout purposes.
 */
function ContentFrameBaseInner<T extends ElementType = "div">(
  {
    as,
    children,
    className,
    innerClassName,
    id,
    "data-testid": testId = "content-frame-base",
    ...props
  }: Props<T>,
  ref: React.ForwardedRef<Element>
) {
  const Component = (as || "div") as ElementType;

  return (
    <Component
      ref={ref}
      className={className}
      id={id}
      data-testid={testId}
      {...(props as Record<string, unknown>)}
    >
      <div className={innerClassName} data-testid={`${testId}-inner`}>
        {children}
      </div>
    </Component>
  );
}

// Cast to any to support polymorphic forwardRef pattern
// The final type assertion provides correct external typing
export const ContentFrameBase = forwardRef(ContentFrameBaseInner as any) as unknown as <
  T extends ElementType = "div"
>(
  props: Props<T> & { ref?: React.ForwardedRef<Element> }
) => JSX.Element;

(ContentFrameBase as any).displayName = "ContentFrameBase";

export default ContentFrameBase;

